import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract-typed.service";
import { Globals } from "../../app.globals";

@Injectable()
export class EmployeesService extends AbstractService<Employee> {
    private apiUrl = "/api/settings/employees";

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getApiUrl(): string {
        return this.apiUrl;
    }

    getEmployee(employeeId: number): Observable<Employee> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/" + employeeId).pipe(
            map((res) => res as Employee),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getEmployees(): Observable<Employee[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getall").pipe(
            map((res) => res as Employee[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getActiveAdminsAndMangers(): Observable<Employee[]> {
        this.showSpinner();
        return this.http
            .get(this.apiUrl + "/active/getalladminsandmanagers")
            .pipe(
                map((res) => res as Employee[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getActiveManagers(): Observable<Employee[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/active/getallmanagers").pipe(
            map((res) => res as Employee[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getActiveEmployees(): Observable<Employee[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/active/getall").pipe(
            map((res) => res as Employee[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getAllRoles(): Observable<Role[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getallroles").pipe(
            map((res) => res as Role[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    updateEmployee(employeeId: number, employee: any): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/" + employeeId, employee).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    updateEmployeeAccesses(employee: EmployeeAccess[]): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/updateaccesses", employee).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    removeEmployeeAccesses(employeeId: number): Observable<any> {
        this.showSpinner();
        return this.http
            .delete(this.apiUrl + "/" + employeeId + "/removeaccesses")
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    addEmployee(employee: any): Observable<any> {
        this.showSpinner();
        return this.http.post(this.apiUrl, employee).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    uploadFile(formData: FormData): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/upload", formData).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    deleteEmployee(employeeId: number): Observable<void> {
        this.showSpinner();
        return this.http.delete(this.apiUrl + "/" + employeeId).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getUnassigned(): Observable<EmployeeAccess[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getunassigned").pipe(
            map((res) => res as EmployeeAccess[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getAssigned(): Observable<EmployeeAccess[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getassigned").pipe(
            map((res) => res as EmployeeAccess[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    assignEmployee(employeeId: number): Observable<any> {
        this.showSpinner();
        return this.http.post(this.apiUrl + "/assign/" + employeeId, null).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    assignEmployees(employeeId: number[]): Observable<any> {
        this.showSpinner();
        return this.http.post(this.apiUrl + "/assign/", employeeId).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    deactivateEmployee(employeeId): Observable<any> {
        this.showSpinner();
        return this.http
            .put(this.apiUrl + "/deactivate/" + employeeId, null)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    activateEmployee(employeeId): Observable<any> {
        this.showSpinner();
        return this.http
            .put(this.apiUrl + "/activate/" + employeeId, null)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    inviteUser(entity: Employee): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/invite/", entity).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    updateProjectAccess(entity: Employee): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/updateProjectAccess", entity).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }
}

export class Role {
    label: string;
    value: string;
}

export class Employee {
    id: number;
    employeeNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isActive: boolean;
    type: string;
    startDate: string;
    endDate: string;
    status: string;
    employeeFullName: string;
    employeeFullNameWithRole: string;
    mobile: string;
    landline: string;
    userClientId: number;
    companyName: string;
}

export class EmployeeAccess extends Employee {
    accessAll: string;
    accessAllProjects: string;
    accessTheirProjects: string;
    accessAllQS: string;
    accessTheirQS: string;
    accessHS: string;
    accessHR: string;
    accessAllEstimating: string;
    accessTheirEstimating: string;
    accessQA: string;
    accessPlants: string;
    accessMaterials: string;
    accessLocations: string;
    accessAdmin: string;
    projectAccesses: number[] = [];

    static map(entity: any): EmployeeAccess {
        var value = new EmployeeAccess();

        value.id = entity.id;
        value.employeeNumber = entity.employeeNumber;
        value.firstName = entity.firstName;
        value.lastName = entity.lastName;
        value.email = entity.email;
        value.role = entity.role;
        value.isActive = entity.isActive;
        value.type = entity.type;
        value.startDate = entity.startDate;
        value.accessAll = entity.accessAll;
        value.accessAllProjects = entity.accessAllProjects;
        value.accessTheirProjects = entity.accessTheirProjects;
        value.accessAllQS = entity.accessAllQS;
        value.accessTheirQS = entity.accessTheirQS;
        value.accessHS = entity.accessHS;
        value.accessHR = entity.accessHR;
        value.accessAllEstimating = entity.accessAllEstimating;
        value.accessTheirEstimating = entity.accessTheirEstimating;
        value.accessQA = entity.accessQA;
        value.accessPlants = entity.accessPlants;
        value.accessMaterials = entity.accessMaterials;
        value.accessAdmin = entity.accessAdmin;
        value.status = entity.status;
        value.accessLocations = entity.accessLocations;
        (entity.projectAccesses || []).forEach((p) => value.projectAccesses.push(p));

        return value;
    }

    static mapAccess(entity: any): EmployeeAccess {
        var value = new EmployeeAccess();

        value.id = entity.id;
        value.employeeNumber = entity.employeeNumber;
        value.firstName = entity.firstName;
        value.lastName = entity.lastName;
        value.email = entity.email;
        value.role = entity.role;
        value.isActive = entity.isActive;
        value.type = entity.type;
        value.startDate = entity.startDate;
        value.accessAll = entity.accessAll;
        value.accessAllProjects = entity.accessAllProjects;
        value.accessTheirProjects = entity.accessTheirProjects;
        value.accessAllQS = entity.accessAllQS;
        value.accessTheirQS = entity.accessTheirQS;
        value.accessHS = entity.accessHS;
        value.accessHR = entity.accessHR;
        value.accessAllEstimating = entity.accessAllEstimating;
        value.accessTheirEstimating = entity.accessTheirEstimating;
        value.accessQA = entity.accessQA;
        value.accessPlants = entity.accessPlants;
        value.accessMaterials = entity.accessMaterials;
        value.accessAdmin = entity.accessAdmin;
        value.status = entity.status;
        value.accessLocations = entity.accessLocations;
        (entity.projectAccesses || []).forEach((p) => value.projectAccesses.push(p.id));

        return value;
    }
}
